import { Fragment, type ReactNode, useMemo } from 'react';
import { type To } from 'react-router';

import { Button, LinkButton } from '@/components/buttons';
import { Separator } from '@/components/Separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';

type Props<T> = {
  size?: 'small' | 'medium' | 'large';
  menuItems: (
    | {
        key: T;
        label: string;
        danger?: boolean;
        inverse?: boolean;
        icon: ReactNode;
        to?: To | false;
        isHidden?: boolean;
      }
    | undefined
    | false
  )[];
  onSelect?: (key: T) => void;
};

const MoreActionsCell = <T extends string>({ size = 'medium', menuItems, onSelect }: Props<T>) => {
  const filteredMenuItems = useMemo(
    () => menuItems.filter((menuItem) => menuItem !== undefined && menuItem !== false),
    [menuItems],
  );

  return (
    <div className="-my-2 flex items-center justify-end whitespace-nowrap">
      {filteredMenuItems.map((menuItem, i) => (
        <Fragment key={menuItem.key}>
          <Tooltip disableHoverablePopup disabled={menuItem.isHidden}>
            <TooltipTrigger
              render={
                menuItem.isHidden ? (
                  <span />
                ) : menuItem.to ? (
                  <LinkButton
                    variant="ghost"
                    aria-label={menuItem.label}
                    danger={menuItem.danger}
                    inverse={menuItem.inverse}
                    size={
                      size === 'small' ? 'icon-small' : size === 'large' ? 'icon-large' : 'icon'
                    }
                    to={menuItem.to}
                    onClick={() => menuItem.key && onSelect?.(menuItem.key)}
                  >
                    {menuItem.icon}
                  </LinkButton>
                ) : (
                  <Button
                    variant="ghost"
                    aria-label={menuItem.label}
                    danger={menuItem.danger}
                    inverse={menuItem.inverse}
                    size={
                      size === 'small' ? 'icon-small' : size === 'large' ? 'icon-large' : 'icon'
                    }
                    onClick={() => menuItem.key && onSelect?.(menuItem.key)}
                  >
                    {menuItem.icon}
                  </Button>
                )
              }
            />
            <TooltipContent side="top">{menuItem.label}</TooltipContent>
          </Tooltip>
          {i < filteredMenuItems.length - 1 && !menuItem.isHidden && (
            <Separator size="small" className="mx-0.5" color="black" orientation="vertical" />
          )}
        </Fragment>
      ))}
    </div>
  );
};

export default MoreActionsCell;
